import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { safeLocalStorage as localStorage, safeSessionStorage as sessionStorage } from "@/lib/safeStorage";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = guest, object = logged in
  const [autoLoginSuppressed, setAutoLoginSuppressed] = useState(() => {
    const available = sessionStorage.setItem("markoo_session_check", "1");
    sessionStorage.removeItem("markoo_session_check");
    return !available || sessionStorage.getItem("rv_logged_out") === "1";
  });

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      setUser(false);
      return null;
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const uid = user && user.id;
  useEffect(() => {
    if (!uid || !localStorage.getItem("rv_token")) {
      return undefined;
    }
    const rotate = async () => {
      try {
        const { data } = await api.post("/auth/token/rotate");
        if (data && data.access_token) localStorage.setItem("rv_token", data.access_token);
      } catch {}
    };
    rotate();
    const id = setInterval(rotate, 30 * 60 * 1000);
    return () => clearInterval(id);
  }, [uid]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data && data.twofa_required) return data;
    if (data && data.access_token) localStorage.setItem("rv_token", data.access_token);
    sessionStorage.removeItem("rv_logged_out");
    setAutoLoginSuppressed(false);
    setUser(data);
    return data;
  };

  const verify2fa = async (challenge, code, remember_device) => {
    const { data } = await api.post("/auth/login/verify-2fa", { challenge, code, remember_device });
    if (data && data.access_token) localStorage.setItem("rv_token", data.access_token);
    sessionStorage.removeItem("rv_logged_out");
    setAutoLoginSuppressed(false);
    setUser(data);
    return data;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    setUser(data);
    return data;
  };

  const logout = async () => {
    setAutoLoginSuppressed(true);
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("rv_token");
    sessionStorage.setItem("rv_logged_out", "1");
    setUser(false);
  };

  const impersonate = async (userId) => {
    const { data } = await api.post(`/admin/impersonate/${userId}`);
    setUser(data);
    return data;
  };

  const stopImpersonation = async () => {
    const { data } = await api.post("/admin/stop-impersonate");
    setUser(data);
    return data;
  };

  return (
    <AuthContext.Provider value={{ user, setUser, autoLoginSuppressed, login, verify2fa, register, logout, refresh, impersonate, stopImpersonation, formatApiErrorDetail }}>
      {children}
    </AuthContext.Provider>
  );
}
